/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-unstable-nested-components, react/prop-types */
import DashboardLayout from "pages/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/Navbars/DashboardNavbar";
import BulkConfigIO from "components/BulkConfigIO";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Public as PublicIcon,
} from "@mui/icons-material";
import MDAlert from "components/MDAlert";
import MDButton from "components/MDButton";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import { protectedResources, siteData } from "authConfig";
import { useCurrentUser } from "context/UserContext";

const API_BASE = `${protectedResources.apiTodoList.endpoint}/companySearchParameters`;
const API_TAXONOMY_ALL = `${protectedResources.apiTodoList.endpoint}/taxonomy/listall`;
const BULK_BASE = `${protectedResources.apiTodoList.endpoint}/bulk-config/company-parameters`;

// ID156/157 — reuse the ID114 (Trusted Sources) domain rules so sources are consistent across the
// app: reduce a URL/bare input to a hostname and validate it's a real domain (rejects "test").
const DOMAIN_RE = /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))*\.[a-z]{2,}$/i;
function parseDomain(raw) {
  const s = (raw || "").trim();
  if (!s) return "";
  try {
    const url = s.includes("://") ? s : `https://${s}`;
    return new URL(url).hostname.replace(/^www\./, "").toLowerCase();
  } catch {
    return s
      .split("/")[0]
      .replace(/^www\./, "")
      .toLowerCase();
  }
}
function isValidDomainStr(domain) {
  return String(domain || "").length <= 253 && DOMAIN_RE.test(String(domain || ""));
}

// ID24 — Context quality gate. Blocks junk like "test" or "1 2 3 4 5". Mirrors the User Search
// screen's rule (word count + distinct meaningful words) so both screens behave consistently.
const CONTEXT_STOP_WORDS = new Set([
  "the",
  "a",
  "an",
  "and",
  "or",
  "of",
  "to",
  "in",
  "on",
  "for",
  "with",
  "is",
  "are",
  "by",
  "at",
  "as",
  "from",
  "this",
  "that",
  "it",
  "its",
]);
function wordCount(text) {
  if (!text || typeof text !== "string") return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
}
function distinctWordCount(text) {
  const words =
    String(text || "")
      .toLowerCase()
      .match(/[a-z0-9]{2,}/g) || [];
  return new Set(words.filter((w) => !CONTEXT_STOP_WORDS.has(w))).size;
}
const MIN_CONTEXT_WORDS = 10;
const MIN_DISTINCT_CONTEXT_WORDS = 3;

// ID157 — each source is its OWN validated chip (not multiple in one box). Type a domain and press
// Enter (or comma) to add it; ID156 rejects anything that isn't a valid domain, with an inline
// message — same behaviour the user gets for Trusted Sources (ID114).
function SourceLinksInput({ value, onChange }) {
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const list = Array.isArray(value) ? value : [];

  const commit = (raw) => {
    const parts = String(raw)
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) return true;
    const next = [...list];
    let ok = true;
    for (let i = 0; i < parts.length; i += 1) {
      const dom = parseDomain(parts[i]);
      if (!isValidDomainStr(dom)) {
        setError(`"${parts[i]}" is not a valid domain. Enter a real domain like "example.com".`);
        ok = false;
        break;
      }
      if (!next.includes(dom)) next.push(dom);
    }
    if (ok) {
      onChange(next);
      setText("");
      setError("");
    }
    return ok;
  };

  return (
    <Box>
      {list.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
          {list.map((s) => (
            <Chip
              key={s}
              label={s}
              size="small"
              onDelete={() => onChange(list.filter((x) => x !== s))}
            />
          ))}
        </Box>
      )}
      <TextField
        size="small"
        fullWidth
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (error) setError("");
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit(text);
          }
        }}
        onBlur={() => {
          if (text.trim()) commit(text);
        }}
        placeholder="example.com  (press Enter to add each source, optional)"
        error={!!error}
        helperText={error || ""}
      />
    </Box>
  );
}

const SUCCESS_ALERT_AUTO_HIDE_MS = 5000;

const PARAMETER_CATEGORIES = [
  "Competitors",
  "Customers/Segments",
  "Suppliers",
  "Material/Commodity",
  "Technology",
  "Premises",
  "Skill Set",
  "Persons",
  "Finance/Economics",
  "Political",
  "Environmental",
  "Social",
  "Legal",
  "Other",
];

const CRITICALITY_OPTIONS = ["", "Low", "Medium", "High", "Extreme"];

const CRITICALITY_COLOR = {
  High: "#d32f2f",
  Extreme: "#b71c1c",
  Medium: "#ed6c02",
  Low: "#757575",
};

const CONTINUITY_OPTIONS = [
  { value: "", label: "—" },
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "partial", label: "Partial" },
];

// Hierarchical country → region/state tree for the geography picker.
// Countries with an empty regions array show as a single checkbox (no sub-levels).
const COUNTRY_REGIONS = [
  { country: "Global", regions: [] },
  {
    country: "Australia",
    regions: [
      "Australian Capital Territory",
      "New South Wales",
      "Northern Territory",
      "Queensland",
      "South Australia",
      "Tasmania",
      "Victoria",
      "Western Australia",
    ],
  },
  {
    country: "United Arab Emirates",
    regions: [
      "Abu Dhabi",
      "Ajman",
      "Dubai",
      "Fujairah",
      "Ras Al Khaimah",
      "Sharjah",
      "Umm Al Quwain",
    ],
  },
  {
    country: "South Africa",
    regions: [
      "Eastern Cape",
      "Free State",
      "Gauteng",
      "KwaZulu-Natal",
      "Limpopo",
      "Mpumalanga",
      "North West",
      "Northern Cape",
      "Western Cape",
    ],
  },
  {
    country: "Philippines",
    regions: [
      "Bicol",
      "Cagayan Valley",
      "Calabarzon",
      "Caraga",
      "Cordillera Administrative Region",
      "Davao",
      "Eastern Visayas",
      "Ilocos",
      "Metro Manila",
      "Mimaropa",
      "Northern Mindanao",
      "SOCCKSARGEN",
      "Western Visayas",
      "Zamboanga Peninsula",
    ],
  },
  {
    country: "Chile",
    regions: [
      "Antofagasta",
      "Araucanía",
      "Arica y Parinacota",
      "Atacama",
      "Aysén",
      "Biobío",
      "Coquimbo",
      "Los Lagos",
      "Los Ríos",
      "Magallanes",
      "Maule",
      "Metropolitana de Santiago",
      "Ñuble",
      "O'Higgins",
      "Tarapacá",
      "Valparaíso",
    ],
  },
  {
    country: "Colombia",
    regions: [
      "Antioquia",
      "Atlántico",
      "Bogotá D.C.",
      "Bolívar",
      "Boyacá",
      "Caldas",
      "Cauca",
      "Córdoba",
      "Cundinamarca",
      "Huila",
      "La Guajira",
      "Magdalena",
      "Meta",
      "Nariño",
      "Norte de Santander",
      "Putumayo",
      "Risaralda",
      "Santander",
      "Sucre",
      "Tolima",
      "Valle del Cauca",
    ],
  },
  {
    country: "Peru",
    regions: [
      "Amazonas",
      "Áncash",
      "Arequipa",
      "Ayacucho",
      "Cajamarca",
      "Callao",
      "Cusco",
      "Ica",
      "Junín",
      "La Libertad",
      "Lambayeque",
      "Lima",
      "Loreto",
      "Madre de Dios",
      "Moquegua",
      "Piura",
      "Puno",
      "San Martín",
      "Tacna",
      "Tumbes",
      "Ucayali",
    ],
  },
  {
    country: "Brazil",
    regions: [
      "Acre",
      "Alagoas",
      "Amapá",
      "Amazonas",
      "Bahia",
      "Ceará",
      "Espírito Santo",
      "Goiás",
      "Maranhão",
      "Mato Grosso",
      "Mato Grosso do Sul",
      "Minas Gerais",
      "Pará",
      "Paraíba",
      "Paraná",
      "Pernambuco",
      "Piauí",
      "Rio de Janeiro",
      "Rio Grande do Norte",
      "Rio Grande do Sul",
      "Rondônia",
      "Santa Catarina",
      "São Paulo",
      "Sergipe",
      "Tocantins",
    ],
  },
  {
    country: "Mexico",
    regions: [
      "Aguascalientes",
      "Baja California",
      "Baja California Sur",
      "Campeche",
      "Chiapas",
      "Chihuahua",
      "Coahuila",
      "Colima",
      "Durango",
      "Guanajuato",
      "Guerrero",
      "Hidalgo",
      "Jalisco",
      "Mexico City",
      "Mexico State",
      "Michoacán",
      "Morelos",
      "Nayarit",
      "Nuevo León",
      "Oaxaca",
      "Puebla",
      "Querétaro",
      "Quintana Roo",
      "San Luis Potosí",
      "Sinaloa",
      "Sonora",
      "Tabasco",
      "Tamaulipas",
      "Veracruz",
      "Yucatán",
      "Zacatecas",
    ],
  },
  {
    country: "Argentina",
    regions: [
      "Buenos Aires",
      "Buenos Aires Province",
      "Catamarca",
      "Chaco",
      "Chubut",
      "Córdoba",
      "Corrientes",
      "Entre Ríos",
      "Formosa",
      "Jujuy",
      "La Pampa",
      "La Rioja",
      "Mendoza",
      "Misiones",
      "Neuquén",
      "Río Negro",
      "Salta",
      "San Juan",
      "San Luis",
      "Santa Cruz",
      "Santa Fe",
      "Santiago del Estero",
      "Tierra del Fuego",
      "Tucumán",
    ],
  },
  {
    country: "United States",
    regions: [
      "Alabama",
      "Alaska",
      "Arizona",
      "Arkansas",
      "California",
      "Colorado",
      "Connecticut",
      "Delaware",
      "Florida",
      "Georgia",
      "Hawaii",
      "Idaho",
      "Illinois",
      "Indiana",
      "Iowa",
      "Kansas",
      "Kentucky",
      "Louisiana",
      "Maine",
      "Maryland",
      "Massachusetts",
      "Michigan",
      "Minnesota",
      "Mississippi",
      "Missouri",
      "Montana",
      "Nebraska",
      "Nevada",
      "New Hampshire",
      "New Jersey",
      "New Mexico",
      "New York",
      "North Carolina",
      "North Dakota",
      "Ohio",
      "Oklahoma",
      "Oregon",
      "Pennsylvania",
      "Rhode Island",
      "South Carolina",
      "South Dakota",
      "Tennessee",
      "Texas",
      "Utah",
      "Vermont",
      "Virginia",
      "Washington",
      "West Virginia",
      "Wisconsin",
      "Wyoming",
    ],
  },
  {
    country: "Canada",
    regions: [
      "Alberta",
      "British Columbia",
      "Manitoba",
      "New Brunswick",
      "Newfoundland and Labrador",
      "Northwest Territories",
      "Nova Scotia",
      "Nunavut",
      "Ontario",
      "Prince Edward Island",
      "Quebec",
      "Saskatchewan",
      "Yukon",
    ],
  },
  { country: "United Kingdom", regions: ["England", "Northern Ireland", "Scotland", "Wales"] },
  {
    country: "Germany",
    regions: [
      "Baden-Württemberg",
      "Bavaria",
      "Berlin",
      "Brandenburg",
      "Bremen",
      "Hamburg",
      "Hesse",
      "Lower Saxony",
      "Mecklenburg-Vorpommern",
      "North Rhine-Westphalia",
      "Rhineland-Palatinate",
      "Saarland",
      "Saxony",
      "Saxony-Anhalt",
      "Schleswig-Holstein",
      "Thuringia",
    ],
  },
  {
    country: "France",
    regions: [
      "Auvergne-Rhône-Alpes",
      "Bourgogne-Franche-Comté",
      "Brittany",
      "Centre-Val de Loire",
      "Corsica",
      "Grand Est",
      "Hauts-de-France",
      "Île-de-France",
      "Normandy",
      "Nouvelle-Aquitaine",
      "Occitanie",
      "Pays de la Loire",
      "Provence-Alpes-Côte d'Azur",
    ],
  },
  {
    country: "Italy",
    regions: [
      "Abruzzo",
      "Apulia",
      "Basilicata",
      "Calabria",
      "Campania",
      "Emilia-Romagna",
      "Friuli-Venezia Giulia",
      "Lazio",
      "Liguria",
      "Lombardy",
      "Marche",
      "Molise",
      "Piedmont",
      "Sardinia",
      "Sicily",
      "Trentino-South Tyrol",
      "Tuscany",
      "Umbria",
      "Veneto",
    ],
  },
  {
    country: "Spain",
    regions: [
      "Andalusia",
      "Aragon",
      "Asturias",
      "Balearic Islands",
      "Basque Country",
      "Canary Islands",
      "Cantabria",
      "Castile and León",
      "Castile-La Mancha",
      "Catalonia",
      "Extremadura",
      "Galicia",
      "La Rioja",
      "Madrid",
      "Murcia",
      "Navarre",
      "Valencia",
    ],
  },
  {
    country: "India",
    regions: [
      "Andhra Pradesh",
      "Assam",
      "Bihar",
      "Chhattisgarh",
      "Goa",
      "Gujarat",
      "Haryana",
      "Himachal Pradesh",
      "Jharkhand",
      "Karnataka",
      "Kerala",
      "Madhya Pradesh",
      "Maharashtra",
      "Manipur",
      "Meghalaya",
      "Mizoram",
      "Nagaland",
      "Odisha",
      "Punjab",
      "Rajasthan",
      "Sikkim",
      "Tamil Nadu",
      "Telangana",
      "Tripura",
      "Uttar Pradesh",
      "Uttarakhand",
      "West Bengal",
    ],
  },
  {
    country: "China",
    regions: [
      "Anhui",
      "Beijing",
      "Chongqing",
      "Fujian",
      "Guangdong",
      "Guangxi",
      "Guizhou",
      "Hebei",
      "Heilongjiang",
      "Henan",
      "Hubei",
      "Hunan",
      "Inner Mongolia",
      "Jiangsu",
      "Jiangxi",
      "Jilin",
      "Liaoning",
      "Qinghai",
      "Shaanxi",
      "Shandong",
      "Shanghai",
      "Shanxi",
      "Sichuan",
      "Tianjin",
      "Tibet",
      "Xinjiang",
      "Yunnan",
      "Zhejiang",
    ],
  },
  {
    country: "Japan",
    regions: [
      "Aichi",
      "Chiba",
      "Fukuoka",
      "Hiroshima",
      "Hokkaido",
      "Hyogo",
      "Ibaraki",
      "Kanagawa",
      "Kyoto",
      "Miyagi",
      "Nagano",
      "Nagasaki",
      "Niigata",
      "Okinawa",
      "Osaka",
      "Saitama",
      "Shizuoka",
      "Tokyo",
    ],
  },
  {
    country: "Indonesia",
    regions: [
      "Aceh",
      "Bali",
      "Banten",
      "Central Java",
      "East Java",
      "East Kalimantan",
      "Jakarta",
      "North Sumatra",
      "Papua",
      "Riau",
      "South Kalimantan",
      "South Sulawesi",
      "South Sumatra",
      "West Java",
      "West Kalimantan",
      "Yogyakarta",
    ],
  },
  {
    country: "Saudi Arabia",
    regions: [
      "Al-Madinah",
      "Asir",
      "Eastern Province",
      "Jazan",
      "Makkah",
      "Najran",
      "Riyadh",
      "Tabuk",
    ],
  },
  {
    country: "Turkey",
    regions: [
      "Adana",
      "Ankara",
      "Antalya",
      "Bursa",
      "Gaziantep",
      "Istanbul",
      "Izmir",
      "Konya",
      "Mersin",
    ],
  },
  { country: "Netherlands", regions: [] },
  { country: "Belgium", regions: [] },
  { country: "Portugal", regions: [] },
  { country: "Greece", regions: [] },
  { country: "Switzerland", regions: [] },
  { country: "Austria", regions: [] },
  { country: "Poland", regions: [] },
  { country: "Sweden", regions: [] },
  { country: "Norway", regions: [] },
  { country: "Denmark", regions: [] },
  { country: "Czech Republic", regions: [] },
  { country: "Hungary", regions: [] },
  { country: "Romania", regions: [] },
  { country: "Russia", regions: [] },
  { country: "Ukraine", regions: [] },
  { country: "Israel", regions: [] },
  { country: "Egypt", regions: [] },
  { country: "Morocco", regions: [] },
  { country: "Nigeria", regions: [] },
  { country: "Kenya", regions: [] },
  { country: "Ethiopia", regions: [] },
  { country: "Ghana", regions: [] },
  { country: "Tanzania", regions: [] },
  { country: "Malaysia", regions: [] },
  { country: "Singapore", regions: [] },
  { country: "Thailand", regions: [] },
  { country: "Vietnam", regions: [] },
  { country: "South Korea", regions: [] },
  { country: "Taiwan", regions: [] },
  { country: "Hong Kong", regions: [] },
  { country: "Bangladesh", regions: [] },
  { country: "Pakistan", regions: [] },
  { country: "New Zealand", regions: [] },
  { country: "Qatar", regions: [] },
  { country: "Kuwait", regions: [] },
  { country: "Bahrain", regions: [] },
  { country: "Oman", regions: [] },
];

// Flat legacy list — kept so existing saved values still display as chips
const GEOGRAPHY_OPTIONS = [
  "Global",
  "North America",
  "Latin America",
  "Europe",
  "Middle East",
  "Africa",
  "APAC",
  "Oceania",
];

// ── GeographyPicker — hierarchical checkbox dialog ────────────────────────
function GeographyPicker({ value = [], onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState([]);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});

  const openDialog = () => {
    setDraft([...value]);
    setSearch("");
    setExpanded({});
    setOpen(true);
  };

  const getCountryState = (entry) => {
    const countryChecked = draft.includes(entry.country);
    if (!entry.regions.length) return { checked: countryChecked, indeterminate: false };
    const selRegions = entry.regions.filter((r) => draft.includes(r)).length;
    // indeterminate: some states selected but country itself not ticked
    if (!countryChecked && selRegions > 0) return { checked: false, indeterminate: true };
    return { checked: countryChecked, indeterminate: false };
  };

  // Toggling a country only adds/removes the country name — never touches individual regions.
  const toggleCountry = (entry) => {
    const { country } = entry;
    setDraft((prev) =>
      prev.includes(country) ? prev.filter((v) => v !== country) : [...prev, country]
    );
  };

  // Toggling a region only adds/removes that region — no parent auto-check.
  const toggleRegion = (_, region) => {
    setDraft((prev) =>
      prev.includes(region) ? prev.filter((v) => v !== region) : [...prev, region]
    );
  };

  const filtered = search.trim()
    ? COUNTRY_REGIONS.filter(
        (e) =>
          e.country.toLowerCase().includes(search.toLowerCase()) ||
          e.regions.some((r) => r.toLowerCase().includes(search.toLowerCase()))
      )
    : COUNTRY_REGIONS;

  const selectedCount = value.length;

  return (
    <>
      <Box
        onClick={disabled ? undefined : openDialog}
        sx={{
          border: "1px solid rgba(0,0,0,0.23)",
          borderRadius: 1,
          p: "6px 12px",
          minHeight: 40,
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.6 : 1,
          "&:hover": disabled ? {} : { borderColor: "rgba(0,0,0,0.87)" },
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
          Geographic regions
        </Typography>
        {value.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ fontSize: 13 }}>
            Click to select regions…
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {value.map((v) => (
              <Chip
                key={v}
                label={v}
                size="small"
                variant="outlined"
                sx={{
                  "&&": {
                    bgcolor: "rgba(2,119,189,0.1) !important",
                    color: "#0277bd !important",
                    borderColor: "rgba(2,119,189,0.4) !important",
                  },
                  "&& .MuiChip-label": { color: "#0277bd !important", fontWeight: 500 },
                }}
              />
            ))}
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {selectedCount > 0
            ? `${selectedCount} region${selectedCount !== 1 ? "s" : ""} selected`
            : "Used to filter news sources. Choose Global for worldwide."}
        </Typography>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PublicIcon sx={{ color: "#52009b" }} />
            Select Geographic Regions
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important", pb: 0 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search country or region…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <Box sx={{ maxHeight: 420, overflowY: "auto", pr: 0.5 }}>
            {filtered.map((entry) => {
              const { checked, indeterminate } = getCountryState(entry);
              const isExpanded = expanded[entry.country] ?? false;
              const showRegions = entry.regions.length > 0;
              const matchSearch = search.trim() !== "";

              return (
                <Box key={entry.country}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      borderRadius: 1,
                      "&:hover": { bgcolor: "rgba(82,0,155,0.04)" },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={() => toggleCountry(entry)}
                      sx={{
                        color: "#52009b",
                        "&.Mui-checked": { color: "#52009b" },
                        "&.MuiCheckbox-indeterminate": { color: "#52009b" },
                      }}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ flex: 1, cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleCountry(entry)}
                    >
                      {entry.country}
                    </Typography>
                    {showRegions && (
                      <IconButton
                        size="small"
                        onClick={() =>
                          setExpanded((prev) => ({ ...prev, [entry.country]: !isExpanded }))
                        }
                        sx={{ color: "text.secondary" }}
                      >
                        {isExpanded || matchSearch ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </Box>
                  {showRegions && (isExpanded || matchSearch) && (
                    <Box sx={{ pl: 3 }}>
                      {entry.regions
                        .filter(
                          (r) => !search.trim() || r.toLowerCase().includes(search.toLowerCase())
                        )
                        .map((region) => (
                          <Box
                            key={region}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              borderRadius: 1,
                              "&:hover": { bgcolor: "rgba(82,0,155,0.04)" },
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={draft.includes(region)}
                              onChange={() => toggleRegion(entry, region)}
                              sx={{
                                color: "#52009b",
                                "&.Mui-checked": { color: "#52009b" },
                                py: 0.25,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ cursor: "pointer", userSelect: "none" }}
                              onClick={() => toggleRegion(entry, region)}
                            >
                              {region}
                            </Typography>
                          </Box>
                        ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
          {draft.length > 0 && (
            <Box sx={{ borderTop: "1px solid rgba(0,0,0,0.08)", pt: 1, mt: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                {draft.length} selected
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {draft.map((v) => (
                  <Chip
                    key={v}
                    label={v}
                    size="small"
                    onDelete={() => {
                      const entry = COUNTRY_REGIONS.find(
                        (e) => e.country === v || e.regions.includes(v)
                      );
                      if (entry && entry.regions.includes(v)) {
                        toggleRegion(entry, v);
                      } else if (entry) {
                        toggleCountry(entry);
                      } else {
                        setDraft((prev) => prev.filter((x) => x !== v));
                      }
                    }}
                    sx={{ fontSize: 11 }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <MDButton
            onClick={() => {
              setDraft([]);
            }}
          >
            Clear all
          </MDButton>
          <Box sx={{ flex: 1 }} />
          <MDButton onClick={() => setOpen(false)}>Cancel</MDButton>
          <MDButton
            variant="contained"
            sx={{ bgcolor: "#52009b", color: "#f7f4fb", "&:hover": { bgcolor: "#3d0075" } }}
            onClick={() => {
              onChange(draft);
              setOpen(false);
            }}
          >
            Apply
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

// GICS® two-level hierarchy: Sector → Industry Groups
const GICS_SECTORS = [
  {
    sector: "Energy",
    groups: ["Energy Equipment & Services", "Oil, Gas & Consumable Fuels"],
  },
  {
    sector: "Materials",
    groups: [
      "Chemicals",
      "Construction Materials",
      "Containers & Packaging",
      "Metals & Mining",
      "Paper & Forest Products",
    ],
  },
  {
    sector: "Industrials",
    groups: [
      "Aerospace & Defense",
      "Building Products",
      "Construction & Engineering",
      "Electrical Equipment",
      "Industrial Conglomerates",
      "Industrial Machinery & Supplies",
      "Trading Companies & Distributors",
      "Air Freight & Logistics",
      "Airlines",
      "Ground Transportation",
      "Marine Transportation",
      "Rail Transportation",
      "Commercial & Professional Services",
      "Environmental & Facilities Services",
      "Human Resource & Employment Services",
      "Research & Consulting Services",
    ],
  },
  {
    sector: "Consumer Discretionary",
    groups: [
      "Auto Parts & Equipment",
      "Automobile Manufacturers",
      "Consumer Electronics",
      "Home Furnishings",
      "Homebuilding",
      "Household Appliances",
      "Leisure Products",
      "Apparel, Accessories & Luxury Goods",
      "Footwear",
      "Textiles",
      "Casinos & Gaming",
      "Hotels, Resorts & Cruise Lines",
      "Leisure Facilities",
      "Restaurants",
      "Education Services",
      "Specialty Retail",
      "Broadline Retail",
      "Internet & Direct Marketing Retail",
    ],
  },
  {
    sector: "Consumer Staples",
    groups: [
      "Agricultural Products & Services",
      "Brewers",
      "Distillers & Vintners",
      "Food Distributors",
      "Food Retail",
      "Packaged Foods & Meats",
      "Soft Drinks & Non-alcoholic Beverages",
      "Tobacco",
      "Household Products",
      "Personal Care Products",
      "Drug Retail",
    ],
  },
  {
    sector: "Health Care",
    groups: [
      "Biotechnology",
      "Health Care Equipment",
      "Health Care Facilities",
      "Health Care Services",
      "Health Care Supplies",
      "Life Sciences Tools & Services",
      "Managed Health Care",
      "Pharmaceuticals",
    ],
  },
  {
    sector: "Financials",
    groups: [
      "Asset Management & Custody Banks",
      "Consumer Finance",
      "Diversified Banks",
      "Diversified Financial Services",
      "Financial Exchanges & Data",
      "Insurance Brokers",
      "Investment Banking & Brokerage",
      "Life & Health Insurance",
      "Mortgage Finance",
      "Multi-line Insurance",
      "Property & Casualty Insurance",
      "Regional Banks",
      "Reinsurance",
      "Specialized Finance",
    ],
  },
  {
    sector: "Information Technology",
    groups: [
      "Application Software",
      "Communications Equipment",
      "Data Processing & Outsourced Services",
      "Electronic Components",
      "Electronic Equipment & Instruments",
      "Electronic Manufacturing Services",
      "IT Consulting & Other Services",
      "Semiconductor Materials & Equipment",
      "Semiconductors",
      "Systems Software",
      "Technology Hardware, Storage & Peripherals",
    ],
  },
  {
    sector: "Communication Services",
    groups: [
      "Advertising",
      "Broadcasting",
      "Cable & Satellite",
      "Integrated Telecommunication Services",
      "Interactive Home Entertainment",
      "Interactive Media & Services",
      "Movies & Entertainment",
      "Publishing",
      "Wireless Telecommunication Services",
    ],
  },
  {
    sector: "Utilities",
    groups: [
      "Electric Utilities",
      "Gas Utilities",
      "Independent Power & Renewable Electricity Producers",
      "Multi-Utilities",
      "Water Utilities",
    ],
  },
  {
    sector: "Real Estate",
    groups: [
      "Diversified REITs",
      "Health Care REITs",
      "Hotel & Resort REITs",
      "Industrial REITs",
      "Office REITs",
      "Real Estate Development",
      "Real Estate Management & Development",
      "Real Estate Services",
      "Residential REITs",
      "Retail REITs",
      "Specialized REITs",
    ],
  },
];

// Legacy flat list — kept so existing saved sector values still display as chips
const SECTOR_OPTIONS = GICS_SECTORS.map((s) => s.sector);

// ── SectorPicker — GICS hierarchical checkbox dialog ─────────────────────
function SectorPicker({ value = [], onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState([]);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});

  const openDialog = () => {
    setDraft([...value]);
    setSearch("");
    setExpanded({});
    setOpen(true);
  };

  const getSectorState = (entry) => {
    const sectorChecked = draft.includes(entry.sector);
    if (!entry.groups.length) return { checked: sectorChecked, indeterminate: false };
    const selGroups = entry.groups.filter((g) => draft.includes(g)).length;
    if (!sectorChecked && selGroups > 0) return { checked: false, indeterminate: true };
    return { checked: sectorChecked, indeterminate: false };
  };

  const toggleSector = (entry) => {
    const { sector } = entry;
    setDraft((prev) =>
      prev.includes(sector) ? prev.filter((v) => v !== sector) : [...prev, sector]
    );
  };

  const toggleGroup = (_, group) => {
    setDraft((prev) => (prev.includes(group) ? prev.filter((v) => v !== group) : [...prev, group]));
  };

  const filtered = search.trim()
    ? GICS_SECTORS.filter(
        (e) =>
          e.sector.toLowerCase().includes(search.toLowerCase()) ||
          e.groups.some((g) => g.toLowerCase().includes(search.toLowerCase()))
      )
    : GICS_SECTORS;

  return (
    <>
      <Box
        onClick={disabled ? undefined : openDialog}
        sx={{
          border: "1px solid rgba(0,0,0,0.23)",
          borderRadius: 1,
          p: "6px 12px",
          minHeight: 40,
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.6 : 1,
          "&:hover": disabled ? {} : { borderColor: "rgba(0,0,0,0.87)" },
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
          Industry sectors
        </Typography>
        {value.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ fontSize: 13 }}>
            Click to select sectors…
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {value.map((v) => (
              <Chip
                key={v}
                label={v}
                size="small"
                variant="outlined"
                sx={{
                  "&&": {
                    bgcolor: "rgba(82,0,155,0.1) !important",
                    color: "#52009b !important",
                    borderColor: "rgba(82,0,155,0.4) !important",
                  },
                  "&& .MuiChip-label": { color: "#52009b !important", fontWeight: 500 },
                }}
              />
            ))}
          </Box>
        )}
        {value.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            {value.length} sector{value.length !== 1 ? "s" : ""} selected
          </Typography>
        )}
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Select Industry Sectors (GICS®)</DialogTitle>
        <DialogContent sx={{ pt: "8px !important", pb: 0 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search sector or industry group…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <Box sx={{ maxHeight: 420, overflowY: "auto", pr: 0.5 }}>
            {filtered.map((entry) => {
              const { checked, indeterminate } = getSectorState(entry);
              const isExpanded = expanded[entry.sector] ?? false;
              const matchSearch = search.trim() !== "";

              return (
                <Box key={entry.sector}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      borderRadius: 1,
                      "&:hover": { bgcolor: "rgba(82,0,155,0.04)" },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={() => toggleSector(entry)}
                      sx={{
                        color: "#52009b",
                        "&.Mui-checked": { color: "#52009b" },
                        "&.MuiCheckbox-indeterminate": { color: "#52009b" },
                      }}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ flex: 1, cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSector(entry)}
                    >
                      {entry.sector}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setExpanded((prev) => ({ ...prev, [entry.sector]: !isExpanded }))
                      }
                      sx={{ color: "text.secondary" }}
                    >
                      {isExpanded || matchSearch ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                  {(isExpanded || matchSearch) && (
                    <Box sx={{ pl: 3 }}>
                      {entry.groups
                        .filter(
                          (g) => !search.trim() || g.toLowerCase().includes(search.toLowerCase())
                        )
                        .map((group) => (
                          <Box
                            key={group}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              borderRadius: 1,
                              "&:hover": { bgcolor: "rgba(82,0,155,0.04)" },
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={draft.includes(group)}
                              onChange={() => toggleGroup(entry, group)}
                              sx={{
                                color: "#52009b",
                                "&.Mui-checked": { color: "#52009b" },
                                py: 0.25,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ cursor: "pointer", userSelect: "none" }}
                              onClick={() => toggleGroup(entry, group)}
                            >
                              {group}
                            </Typography>
                          </Box>
                        ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
          {draft.length > 0 && (
            <Box sx={{ borderTop: "1px solid rgba(0,0,0,0.08)", pt: 1, mt: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                {draft.length} selected
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {draft.map((v) => (
                  <Chip
                    key={v}
                    label={v}
                    size="small"
                    onDelete={() => setDraft((prev) => prev.filter((x) => x !== v))}
                    sx={{ fontSize: 11 }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <MDButton onClick={() => setDraft([])}>Clear all</MDButton>
          <Box sx={{ flex: 1 }} />
          <MDButton onClick={() => setOpen(false)}>Cancel</MDButton>
          <MDButton
            variant="contained"
            sx={{ bgcolor: "#52009b", color: "#f7f4fb", "&:hover": { bgcolor: "#3d0075" } }}
            onClick={() => {
              onChange(draft);
              setOpen(false);
            }}
          >
            Apply
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Fixed data sources — GNews disabled, not user-configurable.
const ALL_NEWS_PROVIDERS = ["gdelt", "bing", "tavily"];

const INGEST_TIER_OPTIONS = [
  { value: "daily", label: "Daily batch" },
  { value: "instant", label: "Instant / frequent" },
];

// The search query is now generated server-side from the FULL description (AI query coach,
// ID101) on save, so the old client-side first-sentence derivation has been removed.

function normaliseTaxonomyLinks(links) {
  if (!Array.isArray(links)) return [];
  return links.map((l) => ({
    taxonomyId: String(l.taxonomyId || ""),
    categoryId: String(l.categoryId || ""),
    label: l.label ? String(l.label) : undefined,
  }));
}

function CompanySearchParameters() {
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const { role, isRiskAdmin } = useCurrentUser();
  const canEditContext = isRiskAdmin;
  const canEditParameters = ["RISK_ADMIN", "SUPER_RISK_ADMIN", "CATEGORY_OWNER"].includes(
    (role || "").toUpperCase()
  );

  const loaded = useRef(false);
  const [context, setContext] = useState({
    companyName: "",
    geographies: [],
    sectors: [],
    website: "",
    websiteContext: "",
    geographyOptions: GEOGRAPHY_OPTIONS,
    sectorOptions: SECTOR_OPTIONS,
    taxonomyNameDefault: null,
    companyNameFromTaxonomyDefault: false,
  });
  const [scrapingWebsite, setScrapingWebsite] = useState(false);
  const [websiteError, setWebsiteError] = useState("");
  const [parameters, setParameters] = useState([]);
  const [successAlert, setSuccessAlert] = useState(null);
  const [taxonomyList, setTaxonomyList] = useState([]);
  const categoryOptionsRef = useRef([]);

  // ── Category editor pop-up (bulk inline edit of one category's parameters) ──
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorCategory, setEditorCategory] = useState("");
  const [rows, setRows] = useState([]); // editable parameter rows
  const [removedIds, setRemovedIds] = useState([]); // existing ids to delete on Save
  const [editorError, setEditorError] = useState("");
  const [savingEditor, setSavingEditor] = useState(false);
  const [expandedRows, setExpandedRows] = useState({}); // rowKey -> advanced section open
  const [confirmDeleteKey, setConfirmDeleteKey] = useState(null); // ID27/110 — row awaiting delete confirm
  // Custom categories the user has added this session (so empty cards still show)
  const [customCategories, setCustomCategories] = useState([]);
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");

  const showSuccessAlert = (message) => setSuccessAlert({ id: Date.now(), message });

  useEffect(() => {
    if (!successAlert) return undefined;
    const timer = window.setTimeout(() => setSuccessAlert(null), SUCCESS_ALERT_AUTO_HIDE_MS);
    return () => clearTimeout(timer);
  }, [successAlert]);

  const fetchContext = useCallback(() => {
    executeRef.current("GET", `${API_BASE}/context`).then((res) => {
      if (res?.success && res.data) {
        setContext({
          companyName: res.data.companyName || "",
          geographies: Array.isArray(res.data.geographies) ? res.data.geographies : [],
          sectors: Array.isArray(res.data.sectors) ? res.data.sectors : [],
          website: res.data.website || "",
          websiteContext: res.data.websiteContext || "",
          geographyOptions: Array.isArray(res.data.geographyOptions)
            ? res.data.geographyOptions
            : GEOGRAPHY_OPTIONS,
          sectorOptions: Array.isArray(res.data.sectorOptions)
            ? res.data.sectorOptions
            : SECTOR_OPTIONS,
          taxonomyNameDefault: res.data.taxonomyNameDefault ?? null,
          companyNameFromTaxonomyDefault: !!res.data.companyNameFromTaxonomyDefault,
        });
      }
    });
  }, []);

  const fetchParameters = useCallback(() => {
    executeRef.current("GET", `${API_BASE}/list`).then((res) => {
      if (res?.success && Array.isArray(res.data)) setParameters(res.data);
    });
  }, []);

  const fetchTaxonomies = useCallback(() => {
    executeRef.current("GET", API_TAXONOMY_ALL).then((res) => {
      if (res?.success && Array.isArray(res.data)) setTaxonomyList(res.data);
    });
  }, []);

  const categoryOptions = useMemo(() => {
    if (!taxonomyList?.length) return [];
    const opts = [];
    taxonomyList.forEach((t) => {
      const tName = t.name || "Taxonomy";
      const tId = t.id;
      (t.Categories || []).forEach((cat) => {
        if (!cat?.id) return;
        opts.push({
          key: `${tId}:${cat.id}`,
          taxonomyId: tId,
          categoryId: cat.id,
          label: `${tName} › ${cat.name || cat.id}`,
        });
      });
    });
    return opts;
  }, [taxonomyList]);
  categoryOptionsRef.current = categoryOptions;

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    fetchContext();
    fetchParameters();
    fetchTaxonomies();
  }, [fetchContext, fetchParameters, fetchTaxonomies]);

  const saveContext = () => {
    executeRef
      .current("PUT", `${API_BASE}/context`, {
        companyName: context.companyName,
        geographies: context.geographies,
        sectors: context.sectors,
        website: context.website,
        websiteContext: context.websiteContext,
      })
      .then((res) => {
        if (res?.success) {
          if (res.data) {
            setContext((prev) => ({
              ...prev,
              companyName: res.data.companyName ?? prev.companyName,
              geographies: Array.isArray(res.data.geographies)
                ? res.data.geographies
                : prev.geographies,
              sectors: Array.isArray(res.data.sectors) ? res.data.sectors : prev.sectors,
              website: res.data.website ?? prev.website,
              websiteContext: res.data.websiteContext ?? prev.websiteContext,
              geographyOptions: Array.isArray(res.data.geographyOptions)
                ? res.data.geographyOptions
                : prev.geographyOptions,
              sectorOptions: Array.isArray(res.data.sectorOptions)
                ? res.data.sectorOptions
                : prev.sectorOptions,
              taxonomyNameDefault: res.data.taxonomyNameDefault ?? prev.taxonomyNameDefault,
              companyNameFromTaxonomyDefault: !!res.data.companyNameFromTaxonomyDefault,
            }));
          } else {
            fetchContext();
          }
          showSuccessAlert("Company context saved.");
        }
      });
  };

  // WS5/ID191 — scrape the first page of the company website and preview the captured context.
  // Nothing is saved until the admin clicks Save context.
  const scrapeWebsite = () => {
    const url = String(context.website || "").trim();
    setWebsiteError("");
    if (!url) {
      setWebsiteError("Enter a company website URL first.");
      return;
    }
    setScrapingWebsite(true);
    executeRef
      .current("POST", `${API_BASE}/context/scrape-website`, { website: url })
      .then((res) => {
        if (res?.success && res.data) {
          setContext((c) => ({
            ...c,
            website: res.data.website || c.website,
            websiteContext: res.data.websiteContext || "",
          }));
          showSuccessAlert("Website read. Review the captured context, then Save context.");
        } else {
          setWebsiteError(res?.message || "Could not read that website.");
        }
      })
      .catch((err) => setWebsiteError(err?.message || "Could not read that website."))
      .finally(() => setScrapingWebsite(false));
  };

  // Group saved parameters by their category for the cards.
  const paramsByCategory = useMemo(() => {
    const map = {};
    parameters.forEach((p) => {
      const c = p.category || "Other";
      (map[c] = map[c] || []).push(p);
    });
    return map;
  }, [parameters]);

  // Standard categories ∪ any category present in saved data ∪ custom ones added this session.
  const allCategoryNames = useMemo(() => {
    const set = new Set(PARAMETER_CATEGORIES);
    parameters.forEach((p) => {
      if (p.category) set.add(p.category);
    });
    customCategories.forEach((c) => set.add(c));
    return Array.from(set);
  }, [parameters, customCategories]);

  const blankRow = (category) => ({
    rowKey: `new_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    id: null,
    name: "",
    criticality: "",
    description: "",
    continuityPlan: "",
    sourceLink: "",
    sourceLinks: [],
    keyValueDriver: false,
    keyDependency: false,
    vulnerabilityThreat: false,
    ingestTier: "daily",
    boardPriorities: [],
    taxonomyLinks: [],
    searchTerms: "",
    tavilyQuery: "",
    category,
  });

  const openCategoryEditor = (category) => {
    const existing = (paramsByCategory[category] || []).map((p) => ({
      rowKey: p.id,
      id: p.id,
      name: p.name || "",
      criticality: p.criticality || "",
      description: p.description || "",
      continuityPlan: p.continuityPlan || "",
      sourceLink: p.sourceLink || "",
      // Use the saved array whenever it exists (even if emptied) so a deleted source can't come back;
      // only fall back to the legacy singular `sourceLink` for old records that never had the array.
      sourceLinks: (Array.isArray(p.sourceLinks)
        ? p.sourceLinks
        : p.sourceLink
        ? [p.sourceLink]
        : []
      )
        .map((s) => parseDomain(s)) // normalise legacy URLs to bare domains (ID156)
        .filter(Boolean),
      keyValueDriver: !!p.keyValueDriver,
      keyDependency: !!p.keyDependency,
      vulnerabilityThreat: !!p.vulnerabilityThreat,
      ingestTier: p.ingestTier === "instant" ? "instant" : "daily",
      boardPriorities: Array.isArray(p.boardPriorities) ? p.boardPriorities : [],
      taxonomyLinks: normaliseTaxonomyLinks(p.taxonomyLinks),
      searchTerms: p.searchTerms || "",
      tavilyQuery: p.tavilyQuery || "",
      category,
    }));
    setEditorCategory(category);
    setRows(existing.length ? existing : [blankRow(category)]);
    setRemovedIds([]);
    setExpandedRows({});
    setEditorError("");
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditorError("");
  };

  const updateRow = (idx, patch) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const addRow = () => setRows((prev) => [...prev, blankRow(editorCategory)]);

  const removeRow = (idx) =>
    setRows((prev) => {
      const target = prev[idx];
      if (target?.id) setRemovedIds((ids) => [...ids, target.id]);
      return prev.filter((_, i) => i !== idx);
    });

  const toggleAdvanced = (key) => setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));

  const saveCategory = async () => {
    setEditorError("");
    // Keep only rows the user actually filled in (ignore blank trailing rows).
    const filled = rows.filter((r) => r.name.trim() || r.description.trim() || r.criticality);
    // Mandatory: Name, Criticality, Context. (ID125 removed the Type field; Continuity & Source optional.)
    const invalid = filled.find((r) => !r.name.trim() || !r.criticality || !r.description.trim());
    if (invalid) {
      if (!invalid.name.trim()) setEditorError("Each parameter needs a Name.");
      else if (!invalid.criticality)
        setEditorError(`Set a Criticality for "${invalid.name.trim()}".`);
      else setEditorError(`Add Context for "${invalid.name.trim()}".`);
      return;
    }
    // ID24 — Context must be specific & meaningful (not junk like "test" / "1 2 3 4 5").
    const thinContext = filled.find(
      (r) =>
        wordCount(r.description) < MIN_CONTEXT_WORDS ||
        distinctWordCount(r.description) < MIN_DISTINCT_CONTEXT_WORDS
    );
    if (thinContext) {
      const wc = wordCount(thinContext.description);
      const dwc = distinctWordCount(thinContext.description);
      setEditorError(
        wc < MIN_CONTEXT_WORDS
          ? `Context for "${thinContext.name.trim()}" needs at least ${MIN_CONTEXT_WORDS} words (currently ${wc}). Describe what to monitor and why it matters.`
          : `Context for "${thinContext.name.trim()}" looks too generic or repetitive (only ${dwc} distinct words). Add specifics — what, where and why it matters.`
      );
      return;
    }
    // ID156 — every source must be a valid domain (block save, like Trusted Sources / ID114).
    const badSource = filled.find((r) =>
      (r.sourceLinks || []).some((s) => s.trim() && !isValidDomainStr(parseDomain(s)))
    );
    if (badSource) {
      setEditorError(
        `"${badSource.name.trim()}" has an invalid source. Each source must be a valid domain like "example.com".`
      );
      return;
    }
    setSavingEditor(true);
    // ID24 — AI quality gate (hard block). The endpoint returns ok=false only when the model judges
    // the context junk / too broad; AI outages return ok=true so they never block a legitimate save
    // (the deterministic word rule above is the offline safety net). Assess all rows in parallel.
    const aiResults = await Promise.all(
      filled.map((r) =>
        executeRef
          .current("POST", `${API_BASE}/assess-context`, {
            name: r.name.trim(),
            context: r.description,
          })
          .then((ai) => ({ r, ai }))
          .catch(() => ({ r, ai: null }))
      )
    );
    const flagged = aiResults.find(({ ai }) => ai?.success === true && ai?.data?.ok === false);
    if (flagged) {
      setSavingEditor(false);
      setEditorError(
        `Context for "${flagged.r.name.trim()}" was flagged: ${
          flagged.ai.data.reason ||
          "make it a specific, meaningful description (what to monitor and why)."
        }`
      );
      return;
    }
    try {
      await Promise.all(
        removedIds.map((id) => executeRef.current("DELETE", `${API_BASE}/delete/${id}`))
      );
      const results = await Promise.all(
        filled.map((r) => {
          // ID101 — leave the query blank so the server generates it from the FULL description
          // (AI query coach). It only regenerates for new rows or when the description changes,
          // so existing queries are preserved on unrelated edits.
          const parsedSources = (r.sourceLinks || []).map((s) => parseDomain(s)).filter(Boolean);
          const body = {
            name: r.name.trim(),
            category: editorCategory,
            description: r.description,
            searchTerms: "",
            tavilyQuery: "",
            criticality: r.criticality,
            continuityPlan: r.continuityPlan || "",
            // Keep the legacy singular field in sync so an emptied/changed source list can't be
            // resurrected from a stale `sourceLink` on the next load.
            sourceLink: parsedSources[0] || "",
            sourceLinks: parsedSources,
            boardPriorities: r.boardPriorities,
            ingestTier: r.ingestTier,
            newsSearchProviders: ALL_NEWS_PROVIDERS,
            taxonomyLinks: normaliseTaxonomyLinks(r.taxonomyLinks),
          };
          const url = r.id ? `${API_BASE}/update/${r.id}` : `${API_BASE}/create`;
          const method = r.id ? "PUT" : "POST";
          return executeRef.current(method, url, body);
        })
      );
      const failure = results.find((res) => res && res.success === false);
      if (failure) {
        setEditorError(failure.message || "Some parameters could not be saved.");
        fetchParameters();
        return;
      }
      closeEditor();
      fetchParameters();
      showSuccessAlert("Collection parameters saved.");
    } catch {
      setEditorError("Could not save. Please try again.");
    } finally {
      setSavingEditor(false);
    }
  };

  const openNewCategory = () => {
    setNewCatName("");
    setNewCatOpen(true);
  };

  const confirmNewCategory = () => {
    const name = newCatName.trim();
    if (!name) return;
    if (!allCategoryNames.includes(name)) {
      setCustomCategories((prev) => [...prev, name]);
    }
    setNewCatOpen(false);
    openCategoryEditor(name);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid item xs={12}>
          <Typography variant="h5" component="h1" gutterBottom>
            Horizon Scan Collection Parameters
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Each parameter becomes an individual news search. Name + search terms are sent to your
            selected news providers on every ingest run.
          </Typography>
          {/* Bulk add/modify: download current parameters as .xlsx, edit, re-import with a preview. */}
          <BulkConfigIO
            title="Company Search Parameters"
            exportUrl={`${BULK_BASE}/export`}
            previewUrl={`${BULK_BASE}/preview`}
            applyUrl={`${BULK_BASE}/apply`}
            filename="company-search-parameters.xlsx"
            onApplied={fetchParameters}
            helpText="Keep each row's ID to modify it; blank the ID to add a new row; set Delete? = Y to remove one. A row simply missing from the file is never deleted."
          />
        </Grid>

        {successAlert ? (
          <Grid item xs={12}>
            <MDAlert
              key={successAlert.id}
              color="success"
              dismissible
              onClose={() => setSuccessAlert(null)}
              sx={{
                mb: 0,
                minHeight: 0,
                py: 0.75,
                px: 1.5,
                fontSize: "0.8125rem",
                lineHeight: 1.4,
                fontWeight: 500,
              }}
            >
              {successAlert.message}
            </MDAlert>
          </Grid>
        ) : null}

        {/* Company context */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Company context
          </Typography>
          <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: 2, borderColor: "divider", boxShadow: "none" }}
          >
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={4}>
                <TextField
                  label="Company name"
                  value={context.companyName}
                  onChange={(e) =>
                    setContext((c) => ({
                      ...c,
                      companyName: e.target.value,
                      companyNameFromTaxonomyDefault: false,
                    }))
                  }
                  disabled={!canEditContext}
                  fullWidth
                  size="small"
                  helperText={
                    canEditContext &&
                    context.companyNameFromTaxonomyDefault &&
                    context.taxonomyNameDefault
                      ? "Defaulting from Taxonomy until you save another name."
                      : undefined
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <GeographyPicker
                  value={context.geographies}
                  onChange={(v) => setContext((c) => ({ ...c, geographies: v }))}
                  disabled={!canEditContext}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SectorPicker
                  value={context.sectors}
                  onChange={(v) => setContext((c) => ({ ...c, sectors: v }))}
                  disabled={!canEditContext}
                />
              </Grid>
            </Grid>

            {/* WS5/ID191 — company website → first-page scrape used as extra Horizon Scan context */}
            <Grid container spacing={2} alignItems="flex-start" sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Company website"
                  placeholder="https://www.example.com"
                  value={context.website}
                  onChange={(e) => {
                    const website = e.target.value;
                    setContext((c) => ({ ...c, website }));
                    setWebsiteError("");
                  }}
                  disabled={!canEditContext}
                  fullWidth
                  size="small"
                  error={!!websiteError}
                  helperText={
                    websiteError ||
                    "We read just the first page and use it as extra context for the top-down Horizon Scan."
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                {canEditContext && (
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={scrapeWebsite}
                    disabled={scrapingWebsite || !String(context.website || "").trim()}
                    fullWidth
                  >
                    {scrapingWebsite ? "Reading website…" : "Fetch from website"}
                  </MDButton>
                )}
              </Grid>
              {context.websiteContext ? (
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "rgba(82,0,155,0.04)",
                      borderColor: "rgba(82,0,155,0.2)",
                    }}
                  >
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      Captured company context (editable — included in the top-down Horizon Scan)
                    </Typography>
                    <TextField
                      value={context.websiteContext}
                      onChange={(e) =>
                        setContext((c) => ({ ...c, websiteContext: e.target.value }))
                      }
                      disabled={!canEditContext}
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={10}
                      size="small"
                      sx={{ mt: 0.75 }}
                    />
                  </Paper>
                </Grid>
              ) : null}
            </Grid>

            {canEditContext && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
                <MDButton
                  backgroundColor="#52009b"
                  variant="contained"
                  color="#52009b"
                  sx={{ color: "#f7f4fb" }}
                  onClick={saveContext}
                >
                  Save context
                </MDButton>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Collection Parameters — category cards */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight={600}>
            Collection Parameters
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
            Your critical dependencies, vulnerabilities, and value drivers. Click a card&apos;s Edit
            tab to manage its parameters.
          </Typography>
          <Grid container spacing={2}>
            {allCategoryNames.map((cat) => {
              const items = paramsByCategory[cat] || [];
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={cat}>
                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      height: "100%",
                      display: "flex",
                      borderColor: "rgba(82,0,155,0.25)",
                    }}
                  >
                    {canEditParameters && (
                      <Box
                        onClick={() => openCategoryEditor(cat)}
                        sx={{
                          bgcolor: "#52009b",
                          color: "#fff",
                          writingMode: "vertical-rl",
                          transform: "rotate(180deg)",
                          px: 0.5,
                          py: 1,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          "&:hover": { bgcolor: "#3d0075" },
                        }}
                      >
                        Edit
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                      <Box
                        sx={{
                          bgcolor: "rgba(82,0,155,0.08)",
                          px: 1.5,
                          py: 1,
                          borderBottom: "1px solid rgba(82,0,155,0.15)",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ color: "#52009b" }}
                          noWrap
                          title={cat}
                        >
                          {cat}
                        </Typography>
                      </Box>
                      <Box sx={{ p: 1.5, maxHeight: 160, overflowY: "auto", flex: 1 }}>
                        {items.length === 0 ? (
                          <Typography variant="body2" color="text.disabled">
                            None
                          </Typography>
                        ) : (
                          items.map((p) => (
                            <Box
                              key={p.id}
                              sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  flexShrink: 0,
                                  bgcolor: CRITICALITY_COLOR[p.criticality] || "#c4c4c4",
                                }}
                              />
                              <Typography variant="body2" noWrap title={p.name}>
                                {p.name}
                              </Typography>
                            </Box>
                          ))
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}

            {/* + ADD NEW */}
            {canEditParameters && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Paper
                  variant="outlined"
                  onClick={openNewCategory}
                  sx={{
                    borderRadius: 2,
                    height: "100%",
                    minHeight: 128,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    borderStyle: "dashed",
                    borderColor: "rgba(82,0,155,0.4)",
                    color: "#52009b",
                    "&:hover": { bgcolor: "rgba(82,0,155,0.04)" },
                  }}
                >
                  <Typography variant="h4" sx={{ lineHeight: 1 }}>
                    +
                  </Typography>
                  <Typography variant="button" fontWeight={700}>
                    ADD NEW
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ px: 2, textAlign: "center", mt: 0.5 }}
                  >
                    Add a new collection parameter category
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      {/* ── Category editor pop-up ── */}
      <Dialog
        open={editorOpen}
        onClose={closeEditor}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: { width: "95vw", maxWidth: 1500 } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography variant="h6" sx={{ color: "#52009b", fontWeight: 700 }}>
            Edit your {editorCategory} collection parameters
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Mandatory: Name, Criticality, Context. Optional: Continuity / Contingency, Source.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          {editorError ? (
            <Typography color="error" variant="body2" sx={{ mb: 1.5 }}>
              {editorError}
            </Typography>
          ) : null}
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ minWidth: 1080 }}>
              {/* header row */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  px: 1,
                  pb: 0.5,
                  fontWeight: 700,
                  color: "#52009b",
                  fontSize: "0.72rem",
                }}
              >
                <Box sx={{ flex: "1.4 1 0", minWidth: 140 }}>Name *</Box>
                <Box sx={{ width: 130 }}>Criticality *</Box>
                <Box sx={{ flex: "2 1 0", minWidth: 200 }}>Context *</Box>
                <Box sx={{ width: 150 }}>Continuity / Contingency</Box>
                <Box sx={{ flex: "1.4 1 0", minWidth: 140 }}>Source</Box>
                <Box sx={{ width: 84 }} />
              </Box>

              {rows.map((row, idx) => (
                <Box
                  key={row.rowKey}
                  sx={{ mb: 1, border: "1px solid rgba(0,0,0,0.08)", borderRadius: 1, p: 1 }}
                >
                  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                    <Box sx={{ flex: "1.4 1 0", minWidth: 140 }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.name}
                        onChange={(e) => updateRow(idx, { name: e.target.value })}
                        placeholder="Name"
                      />
                    </Box>
                    <Box sx={{ width: 130 }}>
                      <TextField
                        size="small"
                        select
                        fullWidth
                        value={row.criticality}
                        onChange={(e) => updateRow(idx, { criticality: e.target.value })}
                      >
                        {CRITICALITY_OPTIONS.map((c) => (
                          <MenuItem key={c || "none"} value={c}>
                            {c || "—"}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Box sx={{ flex: "2 1 0", minWidth: 200 }}>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={1}
                        value={row.description}
                        onChange={(e) => updateRow(idx, { description: e.target.value })}
                        placeholder="Why this is a key dependency, vulnerability or value driver"
                      />
                    </Box>
                    <Box sx={{ width: 150 }}>
                      <TextField
                        size="small"
                        select
                        fullWidth
                        value={row.continuityPlan}
                        onChange={(e) => updateRow(idx, { continuityPlan: e.target.value })}
                      >
                        {CONTINUITY_OPTIONS.map((o) => (
                          <MenuItem key={o.value || "none"} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Box sx={{ flex: "1.4 1 0", minWidth: 140 }}>
                      {/* ID136/156/157 — optional sources; each is its own validated domain chip. */}
                      <SourceLinksInput
                        value={row.sourceLinks}
                        onChange={(v) => updateRow(idx, { sourceLinks: v })}
                      />
                    </Box>
                    <Box sx={{ width: 84, display: "flex", gap: 0.25, justifyContent: "flex-end" }}>
                      <Tooltip title="Advanced (scan cadence, taxonomy)">
                        <IconButton size="small" onClick={() => toggleAdvanced(row.rowKey)}>
                          <ExpandMoreIcon
                            fontSize="small"
                            sx={{
                              transition: "transform 0.2s",
                              transform: expandedRows[row.rowKey] ? "rotate(180deg)" : "none",
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete parameter">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setConfirmDeleteKey(row.rowKey)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* ID27/110 — inline delete confirmation (no blocking popup). */}
                  <Collapse in={confirmDeleteKey === row.rowKey}>
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1.5,
                        p: 1,
                        borderRadius: 1,
                        bgcolor: "#fdecea",
                        border: "1px solid #f5c2c0",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          flex: 1,
                          minWidth: 240,
                        }}
                      >
                        <DeleteIcon fontSize="small" sx={{ color: "#b71c1c" }} />
                        <Typography variant="caption" sx={{ color: "#7f1d1d", fontWeight: 600 }}>
                          Delete &quot;{row.name?.trim() || "this parameter"}&quot;? This cannot be
                          undone. Historical results remain, but all associated trends will be
                          deleted.
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <MDButton
                          size="small"
                          variant="outlined"
                          color="dark"
                          onClick={() => setConfirmDeleteKey(null)}
                          sx={{ textTransform: "none" }}
                        >
                          Cancel
                        </MDButton>
                        <MDButton
                          size="small"
                          variant="contained"
                          color="error"
                          onClick={() => {
                            removeRow(idx);
                            setConfirmDeleteKey(null);
                          }}
                          sx={{ textTransform: "none" }}
                        >
                          Delete
                        </MDButton>
                      </Box>
                    </Box>
                  </Collapse>

                  <Collapse in={!!expandedRows[row.rowKey]}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 1.5, pl: 1 }}>
                      <FormControl size="small" sx={{ minWidth: 170 }}>
                        <InputLabel id={`tier-${row.rowKey}`}>Scan cadence</InputLabel>
                        <Select
                          labelId={`tier-${row.rowKey}`}
                          label="Scan cadence"
                          value={row.ingestTier}
                          onChange={(e) => updateRow(idx, { ingestTier: e.target.value })}
                        >
                          {INGEST_TIER_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Autocomplete
                        multiple
                        sx={{ minWidth: 260, flex: 1 }}
                        options={categoryOptions}
                        value={row.taxonomyLinks
                          .map((l) =>
                            categoryOptions.find(
                              (o) => o.taxonomyId === l.taxonomyId && o.categoryId === l.categoryId
                            )
                          )
                          .filter(Boolean)}
                        onChange={(_, v) =>
                          updateRow(idx, {
                            taxonomyLinks: (v || []).map((o) => ({
                              taxonomyId: o.taxonomyId,
                              categoryId: o.categoryId,
                              label: o.label,
                            })),
                          })
                        }
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(a, b) => a?.key === b?.key}
                        disableCloseOnSelect
                        renderInput={(params) => (
                          <TextField {...params} size="small" label="Taxonomy categories" />
                        )}
                      />
                    </Box>
                  </Collapse>
                </Box>
              ))}

              <MDButton onClick={addRow} sx={{ color: "#52009b", mt: 0.5 }}>
                + Add parameter
              </MDButton>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={closeEditor}>Cancel</MDButton>
          <MDButton
            variant="contained"
            backgroundColor="#52009b"
            color="#52009b"
            sx={{ color: "#f7f4fb" }}
            disabled={savingEditor}
            onClick={saveCategory}
          >
            {savingEditor ? "Saving…" : "Save"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── New category dialog ── */}
      <Dialog open={newCatOpen} onClose={() => setNewCatOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add a new parameter category</DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Category name"
            value={newCatName}
            onChange={(e) => setNewCatName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") confirmNewCategory();
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={() => setNewCatOpen(false)}>Cancel</MDButton>
          <MDButton
            variant="contained"
            backgroundColor="#52009b"
            color="#52009b"
            sx={{ color: "#f7f4fb" }}
            disabled={!newCatName.trim()}
            onClick={confirmNewCategory}
          >
            Continue
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default CompanySearchParameters;
